import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { BuddiesTitle } from '../components/BuddiesTitle';
import { getScreenDimensions, waitForItPromise } from 'src/lib/utils';
import { BuddiesStall } from '../components/BuddiesStall';
import { BuddiesProgress } from '../components/BuddiesProgress';
import {
  buddiesGetEventRewardDetails,
  buddiesGetEventStatus,
  buddiesGetPartyScore,
  buddiesGetPartyScoreAdded,
  buddiesGetCommonProps,
} from 'src/replicant/buddies/buddies.getters';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  buddiesEligibilityThreshold,
  buddiesMilestoneBaseGems,
  buddiesMilestoneThresholds,
  buddiesSchedule,
} from 'src/replicant/buddies/buddies.ruleset';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { buddiesGetPartyPlayers, buddiesGetStage } from '../buddies.utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { BuddiesCake } from '../components/BuddiesCake';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { clamp } from 'src/replicant/utils';
import sounds from 'src/lib/sounds';
import { BuddiesSpeechBubble } from '../components/BuddiesSpeechBubble';
import { BuddiesCheckpoint } from '../components/BuddiesCheckpoint';
import i18n from 'src/lib/i18n/i18n';
import { BuddiesGrandPrize } from '../components/BuddiesGrandPrize';
import { BuddiesMultipliers } from '../components/BuddiesMultipliers';
import { BuddiesBackground } from '../components/BuddiesBackground';
import { buddiesRewardSequence } from '../buddies.sequence';
import { track } from 'src/lib/analytics/utils';
import { BuddiesViewOrigin } from 'src/replicant/buddies/buddies.analytics.schema';
import { BuddiesPartyPlayer } from '../buddies.utils';
import { openUserProfilePopup } from 'src/game/friends/friendsList.sequences';
import uiConfig from 'src/lib/ui/config';

type CloseHandler = (ok: boolean) => void;

export default class PopupBuddiesBaking extends PopupBasicLazy {
  private background: BuddiesBackground;
  private buddiesTitle: BuddiesTitle;
  private stall: BuddiesStall;
  private progress: BuddiesProgress;
  private cake: BuddiesCake;
  private infoButton: ButtonScaleView;
  private speechBubble?: BuddiesSpeechBubble;
  private eventId = '';
  private eventTime = 0;
  private currentStage = 0;
  private multipliers: BuddiesMultipliers;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => {
        opts.close(false);
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.close = opts.close;

    this.background = new BuddiesBackground({
      superview: this.box,
      width: width,
      height: height,
      x: width / 2,
      y: height / 2,
      centerOnOrigin: true,
    });

    this.infoButton = new ButtonScaleView({
      superview: this.box,
      ...uiConfig.buttons.info,
      x: 50,
      y: 50,
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_INFO,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        openPopupPromise('popupBuddiesInfo', {});
      },
    });

    this.buttonClose.updateOpts({
      x: this.box.style.width - 50,
      y: 50,
      zIndex: 1,
    });

    this.stall = new BuddiesStall({
      superview: this.box,
      x: this.box.style.width / 2,
      y: height / 2,
      onInvite: () => this.invitePlayers(),
      onTapPlayer: (player) => this.onTapPlayer(player),
      onTapScore: (player) => this.onTapScore(player),
      onTapPet: (player) => this.onTapPet(player),
    });

    this.buddiesTitle = new BuddiesTitle({
      superview: this.stall,
      x: this.stall.style.width / 2,
      y: 100,
    });

    this.multipliers = new BuddiesMultipliers({
      superview: this.stall,
      x: 80,
      y: 500,
    });

    this.progress = new BuddiesProgress({
      superview: this.stall,
      x: this.stall.style.width / 2,
      y: 328,
      current: 0,
      total: buddiesMilestoneThresholds[buddiesMilestoneThresholds.length - 1],
      checkpoints: [...buddiesMilestoneThresholds.slice(0, -1)],
      onClaim: () => this.claimRewards(),
      onTapCheckpoint: this.onTapCheckpoint.bind(this),
    });

    this.cake = new BuddiesCake({
      superview: this.stall,
      x: 540,
      y: 465,
      scale: 0.6,
    });
    this.cake.onInputStart = this.onTapCake.bind(this);
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...BuddiesCake.getAssets(),
      ...AssetGroup.constructURLs([
        'assets/ui/buddies/common',
        'assets/ui/buddies/popupBaking',
      ]),
      'assets/sounds/music/Buddies_BGM.mp3',
    ]);
  }

  public async init(args: { eventId?: string; origin: BuddiesViewOrigin }) {
    super.init({});

    const eventId = args.eventId || buddiesSchedule.getCurrentEvent(now())?.id;

    if (!eventId) {
      console.warn('No active event found for PopupBuddiesBaking');
      return;
    }

    // The popup must have an eventId to work properly
    this.eventId = eventId;
    this.eventTime = buddiesSchedule.findEventByID(this.eventId)?.start ?? 0;

    // Track the BuddiesView event
    track('BuddiesView', {
      ...buddiesGetCommonProps(user(), now(), this.eventId),
      origin: args.origin,
    });

    this.updateProgress();
    await this.updatePlayers(false);
  }

  public async fadeIn() {
    await super.fadeIn();
    this.buddiesTitle.timer.autoUpdate();
    sounds.music.play('buddies');
    await waitForItPromise(200);
    void this.updatePlayers(true);
    await this.animateProgress();

    // Cleanup last scores added, as user has seen them now
    void StateObserver.invoke.buddiesClearAddedScores();
  }

  public async fadeOut() {
    super.fadeOut();
    sounds.music.play('bgm');
    this.buddiesTitle.timer.stopAutoUpdate();
  }

  private updateProgress(score?: number) {
    const event = user().bakingBuddies?.events[this.eventId];
    if (!event) return;

    // If score is not provided, calculate the current based on last score added.
    if (score === undefined) {
      const totalScore = buddiesGetPartyScore(user(), this.eventTime);
      const lastScoreAdded = buddiesGetPartyScoreAdded(user(), this.eventTime);
      score = Math.max(totalScore - lastScoreAdded, 0);
    }

    this.progress.setProgress(score);

    const { stage, numStages } = buddiesGetStage(score);
    if (this.currentStage === stage) return;
    this.currentStage = stage;
    this.cake.setStage({ stage, numStages });
    this.updateEventStatus();
  }

  private async updatePlayers(animated: boolean) {
    const players = await buddiesGetPartyPlayers(this.eventId);
    this.stall.updatePlayers(players);
    if (animated) this.stall.animatePetsShowUp();
  }

  private async invitePlayers() {
    sounds.sfx.play('ok');
    await openPopupPromise('popupBuddiesInvite', {});
    this.updatePlayers(true);
  }

  private async claimRewards() {
    const details = buddiesGetEventRewardDetails(user(), this.eventId, now());
    if (!details?.canClaimRewards) return;

    sounds.sfx.play('ok');
    this.box.updateOpts({ canHandleEvents: false });

    try {
      const success = await buddiesRewardSequence({ eventId: this.eventId });
      if (success) {
        this.close(true);
      } else {
        this.box.updateOpts({ canHandleEvents: true });
      }
    } catch (e) {
      this.box.updateOpts({ canHandleEvents: true });
    }
  }

  private async animateProgress() {
    const totalScore = buddiesGetPartyScore(user(), this.eventTime);
    const lastScoreAdded = buddiesGetPartyScoreAdded(user(), this.eventTime);
    const fromScore = Math.max(totalScore - lastScoreAdded, 0);
    const toScore = totalScore;
    const diff = toScore - fromScore;
    const interval = clamp(3000 / diff, 30, 300);

    for (let i = 0; i < lastScoreAdded; i++) {
      sounds.sfx.play('progressCounted');
      const score = fromScore + i;
      this.updateProgress(score);
      await waitForItPromise(interval);
    }

    this.updateProgress(toScore);
  }

  private async showSpeechBubble(args: {
    superview: View;
    content: string | Match2Reward[];
    x: number;
    y: number;
    wide?: boolean;
  }) {
    sounds.sfx.play('move');
    if (!this.speechBubble) {
      this.speechBubble = new BuddiesSpeechBubble();
    }
    this.speechBubble.updateOpts({
      superview: args.superview,
      x: args.x,
      y: args.y,
    });
    this.speechBubble.setContent(args.content, { wide: args.wide });
    await this.speechBubble.showAnimated();
  }

  private onTapCheckpoint(checkpoint: BuddiesCheckpoint | BuddiesGrandPrize) {
    const superview = checkpoint.getSuperview();
    if (!superview) return;

    const baseGems = buddiesMilestoneBaseGems[checkpoint.index];
    if (!baseGems) return;

    const details = buddiesGetEventRewardDetails(user(), this.eventId, now());
    const multiplier = details?.multiplier ?? 1;
    const gems = Math.floor(baseGems * multiplier);

    const rewards: Match2Reward[] = [{ type: 'gems', amount: gems }];

    this.showSpeechBubble({
      superview,
      content: rewards,
      x: checkpoint.style.x,
      y: checkpoint.style.y - 90,
    });
  }

  private async onTapPlayer(player: BuddiesPartyPlayer) {
    // Skip opening profile for the main user
    if (player.id === user().id) return;
    sounds.playSound('ok');

    const action = await openUserProfilePopup(player.id, 'buddies', {
      name: player.name,
      photo: player.photo,
    });
    // Close this popup if user has chosen to visit the profile.
    if (action === 'visit') this.close(false);
  }

  private onTapScore(player: BuddiesPartyPlayer) {
    if (player.score >= buddiesEligibilityThreshold) return;
    const pos = this.stall.getPlayerPosition(player);
    if (!pos) return;
    const isSelf = player.id === user().id;
    this.showSpeechBubble({
      superview: this.stall,
      content: i18n(
        isSelf
          ? 'match2.buddies.bakingPopup.minScoreWarning'
          : 'match2.buddies.bakingPopup.buddyScoreWarning',
        {
          value: buddiesEligibilityThreshold,
        },
      ),
      x: pos.x,
      y: pos.y - 380,
      wide: !isSelf,
    });
  }

  private async onTapPet(player: BuddiesPartyPlayer) {
    if (player.score >= buddiesEligibilityThreshold) {
      this.onTapPlayer(player);
    } else {
      this.onTapScore(player);
    }
  }

  private onTapCake() {
    this.showSpeechBubble({
      superview: this.box,
      content: i18n('match2.buddies.bakingPopup.moreStrawberries'),
      x: this.cake.style.x,
      y: this.cake.style.y - 160,
    });
  }

  private updateEventStatus() {
    const details = buddiesGetEventRewardDetails(user(), this.eventId, now());
    const status = buddiesGetEventStatus(user(), this.eventId, now());
    this.progress.updateEventStatus(status, details?.playerScore);
    this.stall.updateEventStatus(status);
    this.buddiesTitle.updateEventStatus(status);
  }
}
